import { addDays, addMonths, DateOnly, format } from '@if/core/date'
import { t } from '@if/core/i18n'
import {
  EFinancingProgram,
  EPaymentFrequency,
  EPaymentMethod,
  EPaymentPlan,
  EWorksheetStatus,
  getEPaymentMethodList,
} from '@if/core/types'
import { formatDate, normalizeDateTime } from '@if/core/utils'
import * as yup from '@if/core/yup'
import {
  FinancingProgramConfig,
  FinancingProgramConfigs,
} from './Constants'
import { FinancingConfigDto, WarrantyPriceTierDto } from '../data/api/configs-api/financing-config-api'

/**
 * Resolves the maximum allowed extended-warranty amount for a given vehicle selling price by walking the
 * configured tiers. Returns the smallest bounded tier whose `maxVehiclePrice` covers the price; if the price
 * exceeds every bounded tier, falls back to the unbounded catch-all tier (`maxVehiclePrice == null`).
 */
export const getWarrantyMaxForVehiclePrice = (
  tiers: WarrantyPriceTierDto[] | undefined,
  vehicleSellingAmount: number | null | undefined,
): number | null => {
  if (!tiers?.length || vehicleSellingAmount == null) return null
  const coveringTier = tiers
    .filter((t) => t.maxVehiclePrice != null && vehicleSellingAmount <= t.maxVehiclePrice)
    .sort((a, b) => a.maxVehiclePrice! - b.maxVehiclePrice!)[0]
  if (coveringTier) return coveringTier.maxWarrantyAmount
  const catchAllTier = tiers.find((t) => t.maxVehiclePrice == null)
  return catchAllTier ? catchAllTier.maxWarrantyAmount : null
}

/**
 * Product-cap validation options. When the feature is on, the merchant worksheet must respect the warranty
 * tier cap (Rule A) and the products total cap (applied directly to amountRequested), both sourced from the
 * per-merchant financing config.
 */
export interface ProductsCapOptions {
  financingConfig?: FinancingConfigDto
  featureEnabled?: boolean
}

export const buildWorksheetBaseSchema = (config: FinancingProgramConfig) =>
  yup.object({
    amountRequested: yup
      .double()
      .required()
      .positive()
      .min(config.minLoanAmount, () => t('common.errors.greaterOrEqual_other', { min: config.minLoanAmount }))
      .max(config.maxLoanAmount, () => t('common.errors.lowerOrEqual_other', { max: config.maxLoanAmount })),

    deliveryOn: yup.string().default(new Date().toDateString()).nonNullable().required(),

    paymentFrequency: yup.mixed<EPaymentFrequency>().default(EPaymentFrequency.Monthly).required(),
    paymentPlanId: yup.mixed<EPaymentPlan>().required().default(EPaymentPlan.RegularDailyInterests),

    firstPaymentOn: yup
      .string()
      .dateOnly()
      .test('deliveryOn-test', function (value?: string | null) {
        const deliveryOn = (this.parent as { deliveryOn: string }).deliveryOn
        const twoMonthsLater = new Date(deliveryOn)
        twoMonthsLater.setMonth(twoMonthsLater.getMonth() + 2)

        return !deliveryOn || !value || (new Date(value) > new Date(deliveryOn) && new Date(value) <= twoMonthsLater)
      }),

    term: yup
      .integer()
      .when('paymentPlanId', ([paymentPlanId], schema) =>
        paymentPlanId === EPaymentPlan.RegularDailyInterests ? schema.min(config.minTerm) : schema.min(1),
      )
      .max(config.maxTerm)
      .nonNullable()
      .required(),

    creditApplicationId: yup.string().default(null).nullable(),

    id: yup.string().default(null).nullable(),

    versionTag: yup.string().default(''),

    isHighEstimate: yup.bool().default(false),
  })

export type WorsheetDetailsDto = yup.InferType<ReturnType<typeof buildWorksheetBaseSchema>>

export const buildEditPersonalLoanWorksheetDtoSchema = (
  hardHitReportReceivedOn: Date,
  finalDecisonMaxAmountFinanced: number,
  merchantPaymentPlanId: EPaymentPlan,
  configOverride?: Partial<FinancingProgramConfig>,
) => {
  const config = { ...FinancingProgramConfigs[EFinancingProgram.Personal], ...configOverride }
  const effectiveMaxLoanAmount = Math.min(config.maxLoanAmount, finalDecisonMaxAmountFinanced)
  const defaultSchema = buildWorksheetBaseSchema({ ...config, maxLoanAmount: effectiveMaxLoanAmount })
  return defaultSchema.shape({
    status: yup.string().required().default(EWorksheetStatus.Draft),

    amountRequested: (defaultSchema.fields.amountRequested as yup.NumberSchema)
      .required()
      .max(effectiveMaxLoanAmount, () => t('common.errors.lowerOrEqual_finalDecisonMaxAmountFinanced'))
      .test(
        'total-merchant-payment-test',
        () => t('common.errors.notEqualMerchantPayment'),
        function (value) {
          const totalMerchantPayment = (this.parent as { merchantPayments: MerchantPayment[] }).merchantPayments.reduce(
            (acc, curr) => Number(acc) + Number(curr.amount),
            0,
          )
          return totalMerchantPayment === value
        },
      ),

    deliveryOn: yup
      .string()
      .default(formatDate(new Date()).toString())
      .required()
      .test(
        'expiresOn-test',
        () => t('common.errors.deliveryOnLowerThanExpiresOn'),
        function (value) {
          const deliveryDate = new Date(value)
          const expiredDate = new Date(hardHitReportReceivedOn)
          expiredDate.setDate(expiredDate.getDate() + 90)
          return deliveryDate <= expiredDate
        },
      )
      .test(
        'deliveryOn-test',
        () => t('common.errors.deliveryOnGreaterOrEqualThanToday'),
        function (value) {
          const deliveryDate = new Date(value)
          deliveryDate.setUTCHours(0, 0, 0, 0)

          const now = new Date()
          now.setUTCHours(0, 0, 0, 0)

          return now <= deliveryDate
        },
      ),
    includeInsurance: yup.bool().default(false),

    addFastPayments: yup.bool().default(false),

    fundConfirmationNote: yup.string().default(null).nullable(),

    isHighEstimate: yup.bool().default(false),

    highEstimateExplainedToClient: yup.bool().default(false),

    merchantPayments: yup.mixed<MerchantPayment[]>().default([]),

    paymentPlanId: yup.mixed<EPaymentPlan>().required().default(merchantPaymentPlanId),
  })
}

const testInsuranceSchemaField = (textContext: yup.TestContext<yup.AnyObject>) => {
  const values = textContext.parent as ProductsInsurance

  const anyFieldProvided = !!values.amount || !!values.term || !!values.police || !!values.provider

  const field = textContext.path.split('.')[1] as keyof ProductsInsurance
  const value = values[field]

  if (anyFieldProvided && !value)
    return textContext.resolve(
      textContext.createError({ path: textContext.path, message: t('mixed.required', { ns: 'validation' }) }),
    )

  return true
}

const testEndDateRequired = (textContext: yup.TestContext<yup.AnyObject>) => {
  const values = textContext.parent as ProductsInsurance

  const anyOtherFieldProvided = !!values.amount || !!values.term || !!values.police || !!values.provider
  const endDateValue = values.endDate

  if (anyOtherFieldProvided && !endDateValue) {
    return textContext.resolve(
      textContext.createError({
        path: textContext.path,
        message: t('mixed.required', { ns: 'validation' }),
      }),
    )
  }

  return true
}
const withEndDateRequired = (schema: yup.ObjectSchema<ProductsInsurance>) => {
  const endDate = schema.fields.endDate as yup.StringSchema<string | null | undefined>
  schema.fields.endDate = endDate.test(function () {
    return testEndDateRequired(this)
  })
  return schema
}

export const buildProductInsuranceSchema = (config: FinancingProgramConfig) =>
  yup.object({
    provider: yup
      .string()
      .nullable()
      .default(null)
      .test(function () {
        return testInsuranceSchemaField(this)
      }),
    police: yup
      .string()
      .nullable()
      .default(null)
      .test(function () {
        return testInsuranceSchemaField(this)
      }),
    term: yup
      .integer()
      .min(config.minTerm)
      .max(config.maxTerm)
      .test(function () {
        return testInsuranceSchemaField(this)
      }),
    amount: yup
      .double()
      .positive()
      .test(function () {
        return testInsuranceSchemaField(this)
      }),
    endDate: yup
      .string()
      .nullable()
      .default(null)
      .dateOnly()
      .when('term', ([term], schema) => {
        if (term && !isNaN(Number(term))) {
          const now = new Date()
          const date = DateOnly.fromComponents(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
          const minDate = format(addMonths(date, Number(term)), 'yyyy-MM-dd')
          const maxDate = format(addDays(addMonths(date, Number(term)), 21), 'yyyy-MM-dd')

          return schema.default(minDate).test('min-date', (val, ctx) => {
            const isExtendedWarranty = ctx.path.includes('extendedWarranty')

            if (isExtendedWarranty) {
              return true 
            }

            if (!val) return true
            if (val < minDate) {
              return ctx.createError({
                message: t('common.errors.invalidMinDate', { minValue: minDate }),
              })
            }
            if (val > maxDate) {
              return ctx.createError({
                message: t('common.errors.invalidMaxDate', { maxValue: maxDate }),
              })
            }
            return true
          })
        }
        return schema
      }),
  })

export type ProductsInsurance = yup.InferType<ReturnType<typeof buildProductInsuranceSchema>>

export const buildMechanicalWarrantyAdditionsSchema = () =>
  yup.object({
    options: yup
      .array()
      .of(yup.string().nonNullable().defined())
      .required()
      .default([]),
    surcharges: yup
      .array()
      .of(yup.string().nonNullable().defined())
      .required()
      .default([]),
  })

const resolveInsuranceMaxTerm = (schema: yup.ObjectSchema<ProductsInsurance>, vehicleFinancingTerm: number) => {
  if (vehicleFinancingTerm)
    schema.fields.term = (
      schema.fields.term as yup.NumberSchema<number | null | undefined, yup.AnyObject, null, 'd'>
    ).max(vehicleFinancingTerm)

  return schema
}

const resolveInsuranceMinTerm = (schema: yup.ObjectSchema<ProductsInsurance>, loanTerm: number) => {
  if (loanTerm)
    schema.fields.term = (
      schema.fields.term as yup.NumberSchema<number | null | undefined, yup.AnyObject, null, 'd'>
    ).min(loanTerm)

  return schema
}

export const getFirstPaymentOnMinMax = (
  deliveryOn: string | Date,
  paymentFrequency: EPaymentFrequency,
): [Date, Date] => {
  const deliveryDate = normalizeDateTime(deliveryOn)
  switch (paymentFrequency) {
    case EPaymentFrequency.Monthly:
      return [addDays(deliveryDate, 2), addDays(deliveryDate, 30)]
    case EPaymentFrequency.BiWeekly:
      return [addDays(deliveryDate, 2), addDays(deliveryDate, 14)]
    case EPaymentFrequency.Single:
      return [addDays(deliveryDate, 2), addDays(deliveryDate, 7)]
    default:
      return [addDays(deliveryDate, 2), addDays(deliveryDate, 5)]
  }
}

const validateProductsAndMechWarrantyDeliveryOnRange = (
  val: string,
  ctx: yup.TestContext<yup.AnyObject>,
): boolean | yup.ValidationError => {
  const deliveryOn = (ctx.parent as ProductsWorksheet | MechanicalWarrantyWorksheet).deliveryOn
  const paymentFrequency = (ctx.parent as ProductsWorksheet | MechanicalWarrantyWorksheet).paymentFrequency
  if (!val || !deliveryOn || !paymentFrequency) return true

  const [minDate, maxDate] = getFirstPaymentOnMinMax(deliveryOn, paymentFrequency)
  const normalizedVal = normalizeDateTime(val)

  if (normalizedVal < minDate) {
    return ctx.createError({ message: t('validation:date.min', { min: formatDate(minDate) }) })
  }

  if (normalizedVal > maxDate) {
    return ctx.createError({ message: t('validation:date.max', { max: formatDate(maxDate) }) })
  }

  return true
}

export const buildProductWorksheetSchema = (
  configOverride?: Partial<FinancingProgramConfig>,
  capOptions: ProductsCapOptions = {},
) => {
  const config = { ...FinancingProgramConfigs[EFinancingProgram.Products], ...configOverride }
  const baseSchema = buildWorksheetBaseSchema(config)
  const baseInsuranceSchema = buildProductInsuranceSchema(config)

  const maxProductsTotal = capOptions.featureEnabled
    ? capOptions.financingConfig?.maxProductsTotalAmount ?? config.maxLoanAmount
    : config.maxLoanAmount

  const extendedWarranty = baseInsuranceSchema

  const replacementOrGapInsurance = withEndDateRequired(baseInsuranceSchema.clone()).when('term', ([term], schema) =>
    resolveInsuranceMinTerm(schema, term as number),
  )

  const creditInsurance = withEndDateRequired(baseInsuranceSchema.clone()).when('term', ([term], schema) =>
    resolveInsuranceMinTerm(schema, term as number),
  )

  return baseSchema
    .shape({
      deliveryOn: yup
        .string()
        .validDate()
        .default(() => formatDate(new Date()))
        .nullable()
        .required(),
      firstPaymentOn: yup
        .string()
        .dateOnly()
        .required()
        .test('deliveryOn-range', (val, ctx) => validateProductsAndMechWarrantyDeliveryOnRange(val, ctx))
        .default(() => formatDate(addDays<Date>(new Date(), 2))),
      amountRequested: (baseSchema.fields.amountRequested as yup.NumberSchema).max(maxProductsTotal, () =>
        t('productsWorksheet.errors.productsTotalExceedsCap', { max: maxProductsTotal }),
      ),
      term: (baseSchema.fields.term as yup.NumberSchema).required(),
      vehicleFinancingTerm: yup.integer().nonNullable().required().min(config.minTerm).max(240),
      vehicleSellingAmount: capOptions.featureEnabled
        ? yup.double().nullable().required().positive()
        : yup.double().nullable().default(null),
      extendedWarranty: extendedWarranty
        .when('vehicleFinancingTerm', ([vehicleFinancingTerm], schema) => {
          return resolveInsuranceMaxTerm(schema, vehicleFinancingTerm as number)
        })
        .nullable(),
      replacementOrGapInsurance: replacementOrGapInsurance
        .when('vehicleFinancingTerm', ([vehicleFinancingTerm], schema) => {
          return resolveInsuranceMaxTerm(schema, vehicleFinancingTerm as number)
        })
        .nullable(),
      creditInsurance: creditInsurance
        .when('vehicleFinancingTerm', ([vehicleFinancingTerm], schema) => {
          return resolveInsuranceMaxTerm(schema, vehicleFinancingTerm as number)
        })
        .nullable(),
    })
    // Rule A — extended warranty capped by the vehicle-price tier.
    .test('warranty-tier-cap', (value, ctx) => {
      if (!capOptions.featureEnabled) return true
      const worksheet = value
      const warrantyAmount = worksheet.extendedWarranty?.amount
      const maxWarranty = getWarrantyMaxForVehiclePrice(
        capOptions.financingConfig?.warrantyMaxByVehiclePriceTier,
        worksheet.vehicleSellingAmount,
      )
      if (warrantyAmount == null || maxWarranty == null) return true
      if (warrantyAmount > maxWarranty) {
        return ctx.createError({
          path: 'extendedWarranty.amount',
          message: t('productsWorksheet.errors.warrantyExceedsTierCap', { max: maxWarranty }),
        })
      }
      return true
    })
}

export type ProductsWorksheet = yup.InferType<ReturnType<typeof buildProductWorksheetSchema>>

export const EditPersonalLoanWorksheetDtoSchema = buildEditPersonalLoanWorksheetDtoSchema(
  new Date(Date.now() + 90 * 24 * 60 * 60 * 1000), // Adds 90 days in milliseconds to the current date
  FinancingProgramConfigs[EFinancingProgram.Personal].maxLoanAmount,
  EPaymentPlan.RegularDailyInterests,
)

export type EditPersonalLoanWorksheetDto = yup.InferType<typeof EditPersonalLoanWorksheetDtoSchema>

export type EditPersonalLoanWorksheetDtoDefaultValue = yup.InferType<typeof EditPersonalLoanWorksheetDtoSchema>

export const merchantPaymentSchema = yup.object({
  merchantId: yup.string().required(),
  amount: yup.double().positive().required(),
  paymentMethod: yup.string().default(EPaymentMethod.BankTransfer).required().oneOf(getEPaymentMethodList()),
  merchantName: yup.string().required(),
})

export type MerchantPayment = yup.InferType<typeof merchantPaymentSchema>

export const buildComputePossibleTermsDtoSchema = (
  maxAmountFinanced: number,
  configOverride?: Partial<FinancingProgramConfig>,
) => {
  const config = { ...FinancingProgramConfigs[EFinancingProgram.Personal], ...configOverride }

  return yup.object({
    merchantId: yup.string().required(),
    paymentPlanId: yup.string().required(),
    interestRate: yup.double().positive().required(),
    hasCoapplicant: yup.bool().required(),
    stateIso: yup.string().required(),
    paymentFrequency: yup.string().oneOf([EPaymentFrequency.Monthly]).required(),
    deliveryOn: yup.string().default(new Date().toDateString()).nonNullable().required(),
    firstPaymentOn: yup.date().test('deliveryOn-test', function (value?: Date) {
      const deliveryOn = (this.parent as { deliveryOn: string }).deliveryOn
      const twoMonthsLater = new Date(deliveryOn)
      twoMonthsLater.setMonth(twoMonthsLater.getMonth() + 2)

      return !deliveryOn || !value || (value > new Date(deliveryOn) && value <= twoMonthsLater)
    }),
    maxPmtAmount: yup.double().positive().required(),
    amountRequested: yup
      .double()
      .required('AR')
      .positive()
      .min(config.minLoanAmount, () => t('common.errors.greaterOrEqual_MinimumLoanAmount'))
      .max(maxAmountFinanced, () => t('common.errors.lowerOrEqual_MaximumLoanAmount')),
    includeInsurance: yup.bool().required(),
  })
}

export const buildMechanicalWarrantyWorksheetSchema = (
  creditApplication?: { isSinglePayment?: boolean },
  configOverride?: Partial<FinancingProgramConfig>,
) => {
  const config = { ...FinancingProgramConfigs[EFinancingProgram.MechanicalWarranty], ...configOverride }
  const baseSchema = buildWorksheetBaseSchema(config)
  const baseAdditionsSchema = buildMechanicalWarrantyAdditionsSchema()
  return baseSchema.shape({
    deliveryOn: yup
      .string()
      .validDate()
      .default(() => formatDate(new Date()))
      .nullable()
      .required(),
    firstPaymentOn: yup
      .string()
      .dateOnly()
      .required()
      .test('deliveryOn-range', (val, ctx) => validateProductsAndMechWarrantyDeliveryOnRange(val, ctx))
      .default(() => formatDate(addDays<Date>(new Date(), 2))),
    term: yup.integer().min(config.minTerm).max(config.maxTerm).required().default(config.minTerm),
    providerId: yup.string().required(),
    policy: yup.string().required().default(null),
    additions: baseAdditionsSchema.default(baseAdditionsSchema.getDefault()),
    mechanicalWarrantyProductId: yup.string().required().default(null),
    paymentFrequency: yup
      .mixed<EPaymentFrequency>()
      .default(creditApplication?.isSinglePayment ? EPaymentFrequency.Single : EPaymentFrequency.Monthly)
      .required()
      .test('valid-payment-frequency', function (value) {
        if (creditApplication?.isSinglePayment) {
          return (
            value === EPaymentFrequency.Single ||
            this.createError({
              message: t('common.errors.singlePaymentOnly'),
            })
          )
        } else {
          return (
            value === EPaymentFrequency.Monthly ||
            value === EPaymentFrequency.BiWeekly ||
            this.createError({
              message: t('common.errors.monthlyOrBiWeeklyOnly'),
            })
          )
        }
      }),
  })
}

export type MechanicalWarrantyWorksheet = yup.InferType<ReturnType<typeof buildMechanicalWarrantyWorksheetSchema>>

export const buildEditIfXpressWorksheetDtoSchema = (
  hardHitReportReceivedOn: Date,
  finalDecisonMaxAmountFinanced: number,
  merchantPaymentPlanId: EPaymentPlan,
  configOverride?: Partial<FinancingProgramConfig>,
) => {
  const config = { ...FinancingProgramConfigs[EFinancingProgram.IfXpress], ...configOverride }
  const effectiveMaxLoanAmount = Math.min(config.maxLoanAmount, finalDecisonMaxAmountFinanced)
  const defaultSchema = buildWorksheetBaseSchema({ ...config, maxLoanAmount: effectiveMaxLoanAmount })
  return defaultSchema.shape({
    status: yup.string().required().default(EWorksheetStatus.Draft),

    amountRequested: (defaultSchema.fields.amountRequested as yup.NumberSchema)
      .required()
      .max(effectiveMaxLoanAmount, () => t('common.errors.lowerOrEqual_finalDecisonMaxAmountFinanced')),

    deliveryOn: yup
      .string()
      .default(formatDate(new Date()).toString())
      .required()
      .test(
        'expiresOn-test',
        () => t('common.errors.deliveryOnLowerThanExpiresOn'),
        function (value) {
          const deliveryDate = new Date(value)
          const expiredDate = new Date(hardHitReportReceivedOn)
          expiredDate.setDate(expiredDate.getDate() + 90)
          return deliveryDate <= expiredDate
        },
      )
      .test(
        'deliveryOn-test',
        () => t('common.errors.deliveryOnGreaterOrEqualThanToday'),
        function (value) {
          const deliveryDate = new Date(value)
          deliveryDate.setUTCHours(0, 0, 0, 0)

          const now = new Date()
          now.setUTCHours(0, 0, 0, 0)

          return now <= deliveryDate
        },
      ),
    includeInsurance: yup.bool().default(false),

    addFastPayments: yup.bool().default(false),

    fundConfirmationNote: yup.string().default(null).nullable(),

    highEstimateExplainedToClient: yup.bool().default(false),

    paymentPlanId: yup.mixed<EPaymentPlan>().required().default(merchantPaymentPlanId),
  })
}

export const EditIfXpressWorksheetDtoSchema = buildEditIfXpressWorksheetDtoSchema(
  new Date(Date.now() + 90 * 24 * 60 * 60 * 1000), // Adds 90 days in milliseconds to the current date
  FinancingProgramConfigs[EFinancingProgram.IfXpress].maxLoanAmount,
  EPaymentPlan.RegularDailyInterests,
)

export type EditIfXpressWorksheetDto = yup.InferType<typeof EditIfXpressWorksheetDtoSchema>

export type EditIfXpressWorksheetDtoDefaultValue = yup.InferType<typeof EditIfXpressWorksheetDtoSchema>

export const buildComputePossibleTermsIfXpressDtoSchema = (configOverride?: Partial<FinancingProgramConfig>) => {
  const config = { ...FinancingProgramConfigs[EFinancingProgram.IfXpress], ...configOverride }

  return yup.object({
    merchantId: yup.string().required(),
    paymentPlanId: yup.string().required(),
    interestRate: yup.double().positive().required(),
    hasCoapplicant: yup.bool().required(),
    stateIso: yup.string().required(),
    paymentFrequency: yup.string().oneOf(Object.values(EPaymentFrequency)).required(),
    deliveryOn: yup.string().default(new Date().toDateString()).nonNullable().required(),
    firstPaymentOn: yup.date().test('deliveryOn-test', function (value?: Date) {
      const deliveryOn = (this.parent as { deliveryOn: string }).deliveryOn
      const twoMonthsLater = new Date(deliveryOn)
      twoMonthsLater.setMonth(twoMonthsLater.getMonth() + 2)

      return !deliveryOn || !value || (value > new Date(deliveryOn) && value <= twoMonthsLater)
    }),
    maxPmtAmount: yup.double().positive().required(),
    amountRequested: yup
      .double()
      .required('AR')
      .positive()
      .min(config.minLoanAmount, () => t('common.errors.greaterOrEqual_MinimumLoanAmount'))
      .max(config.maxLoanAmount, () => t('common.errors.lowerOrEqual_MaximumLoanAmount')),
    includeInsurance: yup.bool().required(),
  })
}

export const computePossibleTermsIfXpressDtoSchema = buildComputePossibleTermsIfXpressDtoSchema()

export type ComputePossibleTermsIfXpressDto = yup.InferType<typeof computePossibleTermsIfXpressDtoSchema>
